import React, { FunctionComponent, ReactElement, useMemo } from 'react';
import {
  IRiskComparisonTablePositionChangesData,
  csvPosition,
  usePositionChangesColumns,
} from './data/positionChangesTable.data';
import {
  IRiskComparisonTableExposureChanges,
  csvExposureChanges,
  useExposureChangesColumns,
} from './data/exposureChangesTable.data';
import {
  IRiskComparisonTableVarChanges,
  csvVarChanges,
  useVarChangesColumns,
} from './data/varChangesTable.data';
import buildComparisonData from './data/buildComparisonTable';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import useActivefund from '../../../../hooks/useActiveFund';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  RiskCompExposureChangesResponse,
  RiskCompPositionChange,
  RiskCompVarBreakdownResponse,
} from './risk-comparison.types';
import { useRiskComparisonTimeSeries } from './queries';
import RaptorLoading from '../../../feedback/RaptorLoading';
import ExportButton from '../../../feedback/ExportButton';
import { ErrorDataMessage } from '../../../feedback/NoDataMessage.component';
import './icon-table-override.css';

type IRiskComparisonType =
  | IRiskComparisonTablePositionChangesData
  | IRiskComparisonTableExposureChanges
  | IRiskComparisonTableVarChanges;

export type ComparisonSection =
  | 'varChanges'
  | 'exposureChanges'
  | 'positionChanges'
  | 'varBreakdown';

interface Props {
  fundId: string;
  referenceDate: string;
  comparisonDate: string;
  selectedSection: ComparisonSection;
  data:
  | RiskCompExposureChangesResponse
  | RiskCompPositionChange[]
  | RiskCompVarBreakdownResponse;
  classes: any;
}

function RiskComparisonTable({
  data,
  fundId,
  referenceDate,
  comparisonDate,
  selectedSection,
}: Props): ReactElement {
  const tableData = useMemo(() => {
    return data
      ? buildComparisonData(
        selectedSection,
        data,
        referenceDate,
        comparisonDate,
      )
      : [];
  }, [data, selectedSection, referenceDate, comparisonDate]);

  const exposureColumns = useExposureChangesColumns(
    referenceDate,
    comparisonDate,
  );

  const varColumns = useVarChangesColumns(comparisonDate, referenceDate);

  const positionChangesColumns = usePositionChangesColumns();

  const tableColumns: CustomColumn<any>[] = useMemo(() => {
    switch (selectedSection) {
      case 'positionChanges':
        return positionChangesColumns;
      case 'exposureChanges':
        return exposureColumns;
      case 'varChanges':
        return varColumns;
      default:
        return positionChangesColumns;
    }
  }, [
    selectedSection,
    positionChangesColumns,
    exposureColumns,
    varColumns,
  ]);

  const activeFundDetails = useActivefund();

  return (
    <GridItem xs={12} card>
      <CustomTable<IRiskComparisonType>
        key={`${selectedSection}-${referenceDate}-${comparisonDate}`}
        smallIcons
        options={{
          paging: false,
          draggable: false,
          search: true,
          exportButton: true,
          rowStyle: {
            padding: 0,
          },
        }}
        columns={tableColumns}
        toolbarComponents={{
          toolbarTitle: (
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-gray-800">
                {activeFundDetails?.name}
              </span>
              <span className="text-sm text-gray-500">
                {selectedSection === 'positionChanges' && 'Position Changes'}
                {selectedSection === 'exposureChanges' && 'Exposure Changes'}
                {selectedSection === 'varChanges' && 'VaR Changes'}
              </span>
            </div>
          ),
        }}
        detailPanel={
          selectedSection === 'positionChanges'
            ? [
              {
                disabled: false,
                render: (rowData: IRiskComparisonType) => {
                  const positionRow =
                    rowData as IRiskComparisonTablePositionChangesData;
                  return (
                    <RiskComparisonTimeSeries
                      positionName={positionRow.positionName}
                      fundId={fundId}
                      referenceDate={referenceDate}
                      comparisonDate={comparisonDate}
                    />
                  );
                },
              },
            ]
            : undefined
        }
        csvFields={(() => {
          switch (selectedSection) {
            case 'positionChanges':
              return csvPosition;
            case 'exposureChanges':
              return csvExposureChanges;
            case 'varChanges':
              return csvVarChanges;
            default:
              return [];
          }
        })()}
        data={tableData}
        showToolbar={true}
        loading={!tableData.length}
      />
    </GridItem>
  );
}
export default RiskComparisonTable;

interface RiskComparisonTimeSeriesProps {
  fundId: string;
  positionName: string;
  referenceDate: string;
  comparisonDate: string;
}

const RiskComparisonTimeSeries: FunctionComponent<
  RiskComparisonTimeSeriesProps
> = ({ fundId, positionName, referenceDate, comparisonDate }) => {
  const {
    data: timeSeriesData,
    isFetching,
    error,
  } = useRiskComparisonTimeSeries(
    fundId,
    positionName,
    referenceDate,
    comparisonDate,
  );

  if (error || timeSeriesData?.status === 'Fail') {
    return <ErrorDataMessage message={'Error Retrieving Data'} />;
  }

  const { min, max } = getPositionRangeWithBuffer(timeSeriesData?.data);

  return (
    <div className="h-64 w-full rounded border border-gray-200 bg-white p-2 px-4 shadow-sm">
      <div className="mb-3 flex w-full items-center justify-between">
        <h3 className="truncate font-semibold text-gray-800">{positionName}</h3>
        {timeSeriesData?.data && (
          <div className="ml-4 shrink-0">
            <ExportButton
              fileName={`${timeSeriesData.position_name}-${timeSeriesData.start_date}-asset-size-time-series-${timeSeriesData.end_date}`}
              exportData={timeSeriesData.data}
              fields={Object.keys(timeSeriesData.data[0])}
              csvTitle={timeSeriesData.position_name}
            />
          </div>
        )}
      </div>

      <div className="h-[calc(100%-2rem)]">
        {isFetching ? (
          <div className="">
            <RaptorLoading
              messages={[
                'Loading Trade Data',
                'This may take a few seconds...',
              ]}
            />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={timeSeriesData?.data}
              margin={{ top: 0, right: 30, left: 20, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tick={{
                  fontSize: 10,
                  fill: '#4B5563',
                  fontWeight: 500,
                  dy: 6,
                  textAnchor: 'end',
                }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}`;
                }}
                angle={-45}
              />
              <YAxis
                dataKey="position"
                domain={[min, max]}
                tick={{
                  fontSize: 10,
                  fill: '#4B5563',
                  fontWeight: 500,
                }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  borderRadius: '0.25rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: '1px solid #E5E7EB',
                  padding: '12px',
                }}
                labelStyle={{
                  color: '#111827',
                  fontWeight: 600,
                  fontSize: '14px',
                  marginBottom: '4px',
                  textTransform: 'capitalize',
                }}
                itemStyle={{
                  color: '#374151',
                  fontSize: '13px',
                  marginTop: '4px',
                  textTransform: 'capitalize',
                }}
                formatter={(value: number) => value.toLocaleString()}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <Line
                type="step"
                dataKey="position"
                stroke="#6366F1"
                strokeWidth={2}
                dot={{ r: 3, fill: '#6366F1' }}
                activeDot={{
                  r: 5,
                  stroke: '#4338CA',
                  strokeWidth: 2,
                  fill: '#6366F1',
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

function getPositionRangeWithBuffer(
  data:
    | {
      date: string;
      position: number;
    }[]
    | undefined,
) {
  if (!data || !data.length) {
    return { min: 0, max: 0 };
  }

  const positions = data.map((d) => d.position);
  const min = Math.min(...positions);
  const max = Math.max(...positions);

  const range = max - min;
  const buffer = range * 0.1;

  return {
    min: min === 0 ? 0 : Math.floor(min - buffer),
    max: max === 0 ? 0 : Math.ceil(max + buffer),
  };
}
